import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { BrandLogo } from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { HERO_BG } from "@/lib/brand";
import { GoogleAuthButton, OrDivider } from "@/components/GoogleAuthButton";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("expired")) {
      setError("Your session expired — please log in again.");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = () => {
    localStorage.setItem("bs_google_plan", "free");
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <div className="mb-10"><BrandLogo /></div>
        <div className="max-w-sm w-full">
          <h1 className="font-display text-3xl font-bold text-slate-900">Welcome back</h1>
          <p className="text-slate-500 mt-2">Log in to your TradeOff workspace.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <GoogleAuthButton label="Continue with Google" onClick={startGoogle} disabled={loading} />
            <OrDivider />
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" className="mt-1" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" className="mt-1" />
            </div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-blue-600 hover:bg-blue-700 h-11">
              {loading ? "Logging in…" : "Log in"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500">
            No account? <Link to="/register" className="text-blue-600 font-medium">Start free</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={HERO_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(22,48,92,0.85), rgba(37,99,235,0.55))" }} />
        <div className="relative h-full flex items-end p-12">
          <p className="text-white font-display text-2xl font-semibold max-w-sm">"From blueprint to invoice without leaving the app."</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
